/**
 * Owner: Masa Bayu
 * File: MarketplaceNav.tsx
 * Date:Time: 2026-10-17 06:10
 * Description: Marketplace top nav — brand, pautan, carian, troli dengan lencana, pill auth.
 *   Label mengikut locale (default English, auto ms-MY ikut negara).
 */

import { useRef, type KeyboardEvent, type ReactElement } from 'react';
import { QX_TAGLINE } from '../../brand/brand-assets';
import { useAccountProfile } from '../../qxcircle/auth/use-account-profile';
import {
  formatCartAriaLabel,
  type MarketplaceLabels,
  type MarketplaceLanguage,
} from './marketplace-locale';

export interface MarketplaceNavProps {
  readonly t: MarketplaceLabels;
  readonly language: MarketplaceLanguage;
  readonly cartCount: number;
  readonly onNavigate: (path: string) => void;
}

const NAV_LINK_KEYS: Readonly<
  { readonly labelKey: keyof MarketplaceLabels['nav']; readonly path: string }[]
> = Object.freeze([
  { labelKey: 'home', path: '/' },
  { labelKey: 'books', path: '/' },
  { labelKey: 'authors', path: '/authors' },
  { labelKey: 'categories', path: '/categories' },
]);

const NAV_LINK_COUNT = NAV_LINK_KEYS.length + 1;

function follow(onNavigate: (path: string) => void, path: string) {
  return (event: { preventDefault: () => void }) => {
    event.preventDefault();
    onNavigate(path);
  };
}

export function MarketplaceNav({
  t,
  language,
  cartCount,
  onNavigate,
}: MarketplaceNavProps): ReactElement {
  const { signedIn, displayName, handle } = useAccountProfile();
  const accountLabel = displayName ?? handle ?? t.nav.accountFallback;
  const navLinkRefs = useRef<Array<HTMLAnchorElement | null>>([]);

  const onNavKeyDown = (event: KeyboardEvent<HTMLAnchorElement>, index: number) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') {
      return;
    }
    event.preventDefault();
    const delta = event.key === 'ArrowRight' ? 1 : -1;
    const next = (index + delta + NAV_LINK_COUNT) % NAV_LINK_COUNT;
    navLinkRefs.current[next]?.focus();
  };

  return (
    <header className="hmk__topnav">
      <a className="hmk__brand" href="/" onClick={follow(onNavigate, '/')}>
        <img className="hmk__brand-mark" src="/brand/qx.png" alt="" />
        <span className="hmk__brand-text">
          <strong>{QX_TAGLINE.top}</strong>
          <small>{QX_TAGLINE.bottom}</small>
        </span>
      </a>

      <nav className="hmk__nav" aria-label={t.nav.ariaLabel}>
        {NAV_LINK_KEYS.map((link, index) => {
          const label = t.nav[link.labelKey];
          const isActive = link.labelKey === 'books';
          return (
            <a
              key={link.labelKey}
              ref={(node) => {
                navLinkRefs.current[index] = node;
              }}
              className="hmk__nav-link"
              data-active={isActive}
              {...(isActive ? { 'aria-current': 'page' as const } : {})}
              href={link.path}
              onClick={follow(onNavigate, link.path)}
              onKeyDown={(event) => {
                onNavKeyDown(event, index);
              }}
            >
              {label}
            </a>
          );
        })}
        <a
          className="hmk__nav-link"
          href="#tentang-kami"
          ref={(node) => {
            navLinkRefs.current[NAV_LINK_KEYS.length] = node;
          }}
          onClick={(event) => {
            event.preventDefault();
            document.getElementById('tentang-kami')?.scrollIntoView({ behavior: 'smooth' });
          }}
          onKeyDown={(event) => {
            onNavKeyDown(event, NAV_LINK_KEYS.length);
          }}
        >
          {t.nav.about}
        </a>
      </nav>

      <div className="hmk__topnav-actions">
        <a
          className="hmk__icon-btn"
          href="/search"
          aria-label={t.nav.searchIcon}
          onClick={follow(onNavigate, '/search')}
        >
          <svg
            viewBox="0 0 24 24"
            width={20}
            height={20}
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.2-3.2" />
          </svg>
        </a>
        <a
          className="hmk__icon-btn"
          href="/cart"
          aria-label={formatCartAriaLabel(cartCount, language)}
          onClick={follow(onNavigate, '/cart')}
        >
          <svg
            viewBox="0 0 24 24"
            width={20}
            height={20}
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M4 5h2l2.2 11h9.6L20 8H7" />
            <circle cx="9.5" cy="19" r="1.4" />
            <circle cx="16.5" cy="19" r="1.4" />
          </svg>
          {cartCount > 0 ? <span className="hmk__cart-badge">{cartCount}</span> : null}
        </a>
        {signedIn ? (
          <a className="hmk__auth-pill" href="/account" onClick={follow(onNavigate, '/account')}>
            {accountLabel}
          </a>
        ) : (
          <a className="hmk__auth-pill" href="/login" onClick={follow(onNavigate, '/login')}>
            {t.nav.logInRegister}
          </a>
        )}
      </div>
    </header>
  );
}
